import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import MovieCard from '@/components/base/MovieCard';
import SectionHeading from '@/components/base/SectionHeading';
import { getMoviesByIds, getSpecialById } from '@/lib/catalog';
import { specials } from '@/mocks/specials';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function SpecialDetailPage() {
  const { id } = useParams<{ id: string }>();
  const special = getSpecialById(id);

  useDocumentMeta({
    title: special ? `专题《${special.title}》- 午夜电影网` : '专题不存在 - 午夜电影网',
    description: special
      ? `${special.description} 专题收录 ${special.movieIds.length} 部影片，由午夜电影网编辑部策展。`
      : '这个专题可能已经被移除，返回专题合集继续浏览其他策展内容。',
    keywords: special ? `${special.title},电影专题,片单,午夜电影网` : '电影专题,午夜电影网',
  });

  if (!special) {
    return (
      <div className="shell pb-20 pt-32">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '专题合集', to: '/specials' }, { label: '专题不存在' }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-layout-grid-line"
            title="专题不存在"
            description="这个专题可能已经下架，或者链接地址有误。返回专题合集，看看其他主题的策展。"
            actionLabel="返回专题合集"
            actionTo="/specials"
          />
        </div>
      </div>
    );
  }

  const movies = getMoviesByIds(special.movieIds);
  const others = specials.filter((item) => item.id !== special.id).slice(0, 3);

  return (
    <div>
      <section className="relative overflow-hidden pb-12 pt-24 md:pb-16 md:pt-32">
        <div className="absolute inset-0">
          <img
            src={special.cover}
            alt={`${special.title}专题封面`}
            className="h-full w-full object-cover object-top opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-background-50/70" />
        </div>

        <div className="shell relative">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '专题合集', to: '/specials' },
              { label: special.title },
            ]}
          />

          <div className="mt-6 max-w-3xl">
            <span className="eyebrow inline-flex rounded bg-primary-500 px-2.5 py-1 text-[10px] text-background-50">
              {special.tag}
            </span>
            <p className="mt-4 text-[11px] tracking-[0.3em] text-primary-500">{special.englishTitle}</p>
            <h1 className="mt-2 font-heading text-4xl font-semibold leading-tight tracking-wide text-foreground-950 md:text-6xl">
              {special.title}
            </h1>
            <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-base md:leading-9">{special.longDescription}</p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-film-line text-primary-500" aria-hidden="true"></i>
                收录 {movies.length} 部影片
              </span>
              <span className="h-3 w-px bg-background-400/50" />
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-primary-500" aria-hidden="true"></i>
                午夜编辑部策展
              </span>
              <span className="h-3 w-px bg-background-400/50" />
              <Link to="/specials" className="transition-colors hover:text-primary-500">
                查看其他专题
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-12 md:py-16">
        <div className="shell">
          <SectionHeading
            eyebrow="Inside this collection"
            title="专题片单"
            description="按观影情绪从轻到重排列。建议按顺序看，也可以直接挑一部开始。"
          />
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {movies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} showGenres />
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="shell">
          <SectionHeading
            eyebrow="More specials"
            title="其他专题"
            description="换个情绪，继续逛。"
            actionLabel="全部专题"
            actionTo="/specials"
          />
          <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-3">
            {others.map((item) => (
              <Link
                key={item.id}
                to={`/specials/${item.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-500/50"
              >
                <div className="h-36 w-full overflow-hidden">
                  <img
                    src={item.cover}
                    alt={item.title}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <p className="text-[10px] tracking-[0.22em] text-primary-500">{item.englishTitle}</p>
                  <h3 className="mt-1.5 font-heading text-lg tracking-wide text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 line-clamp-2 text-xs leading-6 text-foreground-500">{item.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}